import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Filter, 
  MessageSquare, 
  Upload, 
  Settings,
  LogOut,
  Shield,
  Key,
  Book,
  BarChart3
} from 'lucide-react';
import BiharLogin from '../components/bihar/BiharLogin';
import BiharManualExplorer from '../components/bihar/BiharManualExplorer';
import BiharAIInsightsEnhanced from '../components/bihar/BiharAIInsightsEnhanced';
import BiharDataImport from '../components/bihar/BiharDataImport';
import BiharSettings from '../components/bihar/BiharSettings';
import BiharDocs from '../components/bihar/BiharDocs';
import BiharAnalyticsComponent from '../components/bihar/BiharAnalytics';

export default function BiharAnalytics() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('manual');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/bihar-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify-session' })
      });

      const result = await response.json();
      setAuthenticated(result.authenticated);
    } catch (error) {
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    setAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/bihar-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'logout' })
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading Bihar Analytics...</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <BiharLogin onLoginSuccess={handleLoginSuccess} />;
  }

  const tabs = [
    { id: 'manual', label: 'Manual Explorer', icon: Filter },
    { id: 'ai', label: 'AI Insights', icon: MessageSquare },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'import', label: 'Data Import', icon: Upload },
    { id: 'docs', label: 'Docs', icon: Book },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 fixed top-0 left-0 right-0 z-50 shadow-lg">
        <div className="w-full px-6 py-2 flex items-center justify-between">
          {/* Logo & Title */}
          <div className="flex items-center space-x-3">
            <img 
              src="/assets/images/splink-logo-trnsp.png" 
              alt="Splink Logo" 
              className="h-8 w-auto"
            />
            <div className="border-l border-slate-600 pl-3">
              <h1 className="text-lg font-bold text-white">Bihar Sports Analytics</h1>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center space-x-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50'
                      : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex items-center space-x-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
          >
            <LogOut className="w-3 h-3" />
            <span>Logout</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-14 max-w-[1920px] mx-auto px-6 py-6">
        {activeTab === 'manual' && <BiharManualExplorer />}
        {activeTab === 'ai' && <BiharAIInsightsEnhanced />}
        {activeTab === 'analytics' && <BiharAnalyticsComponent />}
        {activeTab === 'import' && <BiharDataImport />}
        {activeTab === 'docs' && <BiharDocs />}
        {activeTab === 'settings' && <BiharSettings />}
      </main>
    </div>
  );
}
